import { Link, useParams } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'motion/react';
import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../components/Icon.tsx';
import { EASE, Reveal } from '../components/motion.tsx';
import { Button, Callout, Chip, Field, PageHead, Skeleton, StatusChip, useToast } from '../components/ui.tsx';
import { get, post, qs } from '../lib/api.ts';
import { date, minutes, time, todayIn } from '../lib/format.ts';
import { useMe } from '../lib/session.ts';

interface Day {
  date: string;
  schedule: { kind: string; shift: { code: string; start: string; end: string } | null } | null;
  timesheet: null | { clockInAt: string | null; clockOutAt: string | null; lateMinutes: number; earlyMinutes: number; workedMinutes: number; status: string; flags: string[]; lockedByRunId: string | null; clockInEventId: string | null; clockOutEventId: string | null };
}
interface Data {
  timeZone: string;
  days: Day[];
  events: { id: string; occurredAt: string; source: string; direction: string | null; voidedAt: string | null; withinGeofence: boolean | null; placeCheck: 'geofence' | 'network' | null; faceMatchScore: number | null }[];
}
interface PersonBrief {
  name: string;
  employeeNo: string | null;
}

const monthBounds = (ym: string) => {
  const [y, m] = ym.split('-').map(Number) as [number, number];
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return { from: `${ym}-01`, to: `${ym}-${String(last).padStart(2, '0')}` };
};

export function Timesheet() {
  const { employeeId } = useParams({ strict: false }) as { employeeId: string };
  return <TimesheetView employeeId={employeeId} />;
}

export function TimesheetView({ employeeId, embedded }: { employeeId: string; embedded?: boolean }) {
  const me = useMe().data!;
  const [ym, setYm] = useState(() => todayIn(me.employee?.timeZone ?? undefined).slice(0, 7));
  const { from, to } = monthBounds(ym);
  const q = useQuery({ queryKey: ['timesheets', employeeId, ym], queryFn: () => get<Data>(`/api/attendance/timesheets${qs({ employeeId, from, to })}`) });
  const person = useQuery({ queryKey: ['person', employeeId], queryFn: () => get<PersonBrief>(`/api/people/${employeeId}`), enabled: !embedded });
  const [open, setOpen] = useState<string | null>(null);

  const summary = useMemo(() => {
    const ds = q.data?.days ?? [];
    const worked = ds.filter((d) => d.timesheet?.clockInAt);
    return {
      present: worked.length,
      scheduled: ds.filter((d) => d.schedule?.kind === 'shift').length,
      late: ds.reduce((n, d) => n + (d.timesheet?.lateMinutes ?? 0), 0),
      missing: ds.filter((d) => d.schedule?.kind === 'shift' && d.timesheet?.status === 'missing' && d.date < todayIn(q.data?.timeZone)).length,
      hours: Math.round(ds.reduce((n, d) => n + (d.timesheet?.workedMinutes ?? 0), 0) / 60),
    };
  }, [q.data]);

  const shift = (n: number) => {
    const [y, m] = ym.split('-').map(Number) as [number, number];
    const d = new Date(Date.UTC(y, m - 1 + n, 1));
    setYm(d.toISOString().slice(0, 7));
  };
  const firstDow = new Date(`${from}T00:00:00Z`).getUTCDay();
  const lead = (firstDow + 6) % 7; // Monday-first
  const tz = q.data?.timeZone;
  const monthLabel = new Date(`${from}T00:00:00Z`).toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });

  return (
    <div>
      {!embedded ? (
        <PageHead
          compact
          eyebrow={person.data ? `Timesheet · ${person.data.employeeNo ?? ''}` : 'Timesheet'}
          title={
            <>
              {person.data?.name ?? '…'}, <em>{monthLabel}.</em>
            </>
          }
          aside={
            <div className="row" style={{ ['--gap' as string]: '4px' }}>
              <Button variant="ghost" size="sm" iconOnly icon="chevronLeft" aria-label="Previous month" onClick={() => shift(-1)} />
              <span className="mono" style={{ minWidth: 80, textAlign: 'center' }}>
                {ym}
              </span>
              <Button variant="ghost" size="sm" iconOnly icon="chevronRight" aria-label="Next month" onClick={() => shift(1)} />
              <PrintLink employeeId={employeeId} month={ym} />
            </div>
          }
        />
      ) : (
        <div className="row row--between" style={{ marginBottom: 16 }}>
          <h2 className="display" style={{ fontSize: 'var(--step-3)' }}>
            {monthLabel}
          </h2>
          <div className="row" style={{ ['--gap' as string]: '4px' }}>
            <Button variant="ghost" size="sm" iconOnly icon="chevronLeft" aria-label="Previous month" onClick={() => shift(-1)} />
            <Button variant="ghost" size="sm" iconOnly icon="chevronRight" aria-label="Next month" onClick={() => shift(1)} />
            <PrintLink employeeId={employeeId} month={ym} />
          </div>
        </div>
      )}

      <Reveal>
        <Reveal.Item>
          <div className="ledger" style={{ ['--cols' as string]: 4, marginBottom: 'var(--s-6)' }}>
            <div className="ledger__cell">
              <div className="ledger__value">
                {summary.present}
                <small>/ {summary.scheduled}</small>
              </div>
              <div className="ledger__label eyebrow">Days present</div>
            </div>
            <div className="ledger__cell">
              <div className="ledger__value">{summary.hours}<small>h</small></div>
              <div className="ledger__label eyebrow">Hours worked</div>
            </div>
            <div className="ledger__cell">
              <div className="ledger__value" style={{ color: summary.late ? 'var(--clay)' : undefined }}>
                {summary.late}
                <small>min</small>
              </div>
              <div className="ledger__label eyebrow">Late, total</div>
            </div>
            <div className="ledger__cell">
              <div className="ledger__value" style={{ color: summary.missing ? 'var(--rose)' : undefined }}>
                {summary.missing}
              </div>
              <div className="ledger__label eyebrow">Missing days</div>
            </div>
          </div>
        </Reveal.Item>
        <Reveal.Item>
          <div className="cal" role="grid" aria-label={`Timesheet ${monthLabel}`}>
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
              <div key={d} className="cal__dow eyebrow">
                {d}
              </div>
            ))}
            {Array.from({ length: lead }, (_, i) => (
              <div key={`pad-${i}`} />
            ))}
            {q.isLoading
              ? Array.from({ length: 30 }, (_, i) => <Skeleton key={i} h={92} />)
              : q.data?.days.map((d) => {
                  const t = d.timesheet;
                  const off = d.schedule?.kind && d.schedule.kind !== 'shift';
                  const future = d.date > todayIn(tz);
                  return (
                    <button key={d.date} type="button" className="cal__day" data-muted={off || future} onClick={() => setOpen(d.date)} aria-label={`${date(d.date, { weekday: true })}: ${t?.status ?? d.schedule?.kind ?? 'no schedule'}`}>
                      <div className="row row--between" style={{ alignItems: 'start' }}>
                        <span className="cal__num" style={{ color: d.date === todayIn(tz) ? 'var(--accent-text)' : undefined }}>
                          {Number(d.date.slice(8))}
                        </span>
                        {t?.lockedByRunId ? <Icon name="lock" width={12} height={12} className="faint" /> : null}
                      </div>
                      {off ? (
                        <span className="faint">{d.schedule!.kind === 'day_off' ? 'Day off' : d.schedule!.kind === 'leave' ? 'Leave' : 'Holiday'}</span>
                      ) : t?.clockInAt || t?.clockOutAt ? (
                        <div className="stack" style={{ ['--gap' as string]: '4px' }}>
                          <span className="cal__times num">
                            {time(t.clockInAt, tz)}–{time(t.clockOutAt, tz)}
                          </span>
                          {t.lateMinutes ? <span style={{ color: 'var(--clay)' }}>+{t.lateMinutes}m late</span> : t.flags.includes('no_clock_out') ? <span style={{ color: 'var(--ochre)' }}>no clock-out</span> : <span style={{ color: 'var(--sage)' }}>on time</span>}
                        </div>
                      ) : future ? (
                        <span className="faint mono">{d.schedule?.shift?.code ?? ''}</span>
                      ) : d.schedule?.kind === 'shift' ? (
                        <span style={{ color: 'var(--rose)' }}>no punches</span>
                      ) : null}
                    </button>
                  );
                })}
          </div>
        </Reveal.Item>
      </Reveal>
      <DaySheet open={open} onClose={() => setOpen(null)} data={q.data} employeeId={employeeId} canEdit={me.access.attendanceAdmin} self={me.employee?.id === employeeId} />
    </div>
  );
}

/** The printable timesheet (legacy's monthly PDF) for the month on screen, as a payroll period. */
function PrintLink({ employeeId, month }: { employeeId: string; month: string }) {
  return (
    <Link to="/attendance/$employeeId/print" params={{ employeeId }} search={{ month }} className="btn btn--sm" style={{ marginLeft: 8 }}>
      <Icon name="file" />
      Print / PDF
    </Link>
  );
}

function DaySheet({ open, onClose, data, employeeId, canEdit, self }: { open: string | null; onClose: () => void; data?: Data; employeeId: string; canEdit: boolean; self: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [voiding, setVoiding] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const day = data?.days.find((d) => d.date === open);
  const tz = data?.timeZone;
  const events = (data?.events ?? []).filter((e) => open && (time(e.occurredAt, tz) && new Intl.DateTimeFormat('en-CA', { timeZone: tz }).format(new Date(e.occurredAt)) === open));
  const voidM = useMutation({
    mutationFn: (id: string) => post(`/api/attendance/events/${id}/void`, { reason }),
    onSuccess: () => {
      toast({ title: 'Punch voided', body: 'The day was re-derived from the remaining punches.' });
      setVoiding(null);
      setReason('');
      void qc.invalidateQueries({ queryKey: ['timesheets', employeeId] });
    },
  });
  return createPortal(
    <AnimatePresence>
      {open && day ? (
        <>
          <motion.div className="scrim" onClick={onClose} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} />
          <motion.aside className="sheet" role="dialog" aria-modal="true" aria-label={`Details for ${date(open)}`} initial={{ x: 40, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: 30, opacity: 0 }} transition={{ duration: 0.42, ease: EASE }}>
            <div style={{ padding: 28 }} className="stack">
              <div className="row row--between">
                <span className="eyebrow">{date(open, { weekday: true })}</span>
                <Button variant="ghost" iconOnly icon="x" aria-label="Close" onClick={onClose} />
              </div>
              <h2 className="display" style={{ fontSize: 'var(--step-4)' }}>
                {day.timesheet?.clockInAt ? (
                  <>
                    {time(day.timesheet.clockInAt, tz)} <em className="faint">to</em> {time(day.timesheet.clockOutAt, tz)}
                  </>
                ) : day.schedule?.kind && day.schedule.kind !== 'shift' ? (
                  <em>{day.schedule.kind.replace('_', ' ')}</em>
                ) : (
                  <em>No punches</em>
                )}
              </h2>
              <div className="row row--wrap">
                {day.timesheet ? <StatusChip status={day.timesheet.status} /> : null}
                {day.schedule?.shift ? (
                  <Chip>
                    {day.schedule.shift.code} {day.schedule.shift.start}–{day.schedule.shift.end}
                  </Chip>
                ) : null}
                {day.timesheet?.lockedByRunId ? <Chip tone="info">Locked by payroll</Chip> : null}
              </div>
              {day.timesheet ? (
                <dl className="dl">
                  <dt>Late</dt>
                  <dd>{minutes(day.timesheet.lateMinutes)}</dd>
                  <dt>Left early</dt>
                  <dd>{minutes(day.timesheet.earlyMinutes)}</dd>
                  <dt>Worked</dt>
                  <dd>{minutes(day.timesheet.workedMinutes)}</dd>
                </dl>
              ) : null}
              <div className="section-label eyebrow" style={{ margin: '12px 0 0' }}>
                Punches ({events.length})
              </div>
              <ul className="list-plain">
                {events.map((e) => (
                  <li key={e.id} className="mini-row" style={{ opacity: e.voidedAt ? 0.45 : 1 }}>
                    <span className="mono num" style={{ fontSize: 15, color: 'var(--ink-1)', textDecoration: e.voidedAt ? 'line-through' : undefined }}>
                      {time(e.occurredAt, tz)}
                    </span>
                    <span className="faint">{e.source.replace('_', ' ')}</span>
                    {e.id === day.timesheet?.clockInEventId ? <Chip tone="ok">used as in</Chip> : e.id === day.timesheet?.clockOutEventId ? <Chip tone="ok">used as out</Chip> : null}
                    {e.placeCheck ? <span className="faint" title={e.placeCheck === 'network' ? 'Passed the place check from a trusted office network, not GPS' : 'Passed the place check inside the office geofence'}>{e.placeCheck === 'network' ? 'office network' : 'in geofence'}</span> : null}
                    {e.faceMatchScore ? <span className="mini-row__aside">match {e.faceMatchScore.toFixed(2)}</span> : null}
                    {canEdit && !e.voidedAt && !day.timesheet?.lockedByRunId ? (
                      <Button size="sm" variant="ghost" style={{ marginLeft: e.faceMatchScore ? 8 : 'auto' }} onClick={() => setVoiding(e.id)}>
                        Void
                      </Button>
                    ) : null}
                  </li>
                ))}
                {!events.length ? <li className="faint">No punches recorded.</li> : null}
              </ul>
              {voiding ? (
                <div className="stack panel" style={{ padding: 16 }}>
                  <Field label="Why void this punch?">{(p) => <input {...p} className="input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Duplicate scan from a colleague's finger" />}</Field>
                  <div className="row">
                    <Button variant="danger" size="sm" loading={voidM.isPending} disabled={reason.length < 5} onClick={() => voidM.mutate(voiding)}>
                      Void punch
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setVoiding(null)}>
                      Keep it
                    </Button>
                  </div>
                </div>
              ) : null}
              {self && day.schedule?.kind === 'shift' && open <= todayIn(tz) ? (
                <Callout tone="info">Something wrong with this day? Request a correction from <a className="link" href="/time">My time & leave</a>; your manager approves it and the day is re-derived.</Callout>
              ) : null}
            </div>
          </motion.aside>
        </>
      ) : null}
    </AnimatePresence>,
    document.body,
  );
}
